import type { Product, ProductType, RewardStatus, SystemSettings, User } from '../state/types'

function floorJPY(value: number) {
  return Math.floor(value)
}

function normalizeRate(value: number) {
  return Number(value.toFixed(10))
}

export function getEffectiveAgencyRate(
  settings: Pick<SystemSettings, 'agencyRate'>,
  agency?: Pick<User, 'agencyRateOverride'>,
): number {
  return agency?.agencyRateOverride ?? settings.agencyRate
}

export function getEffectiveConnectorRate(
  settings: Pick<SystemSettings, 'connectorRate'>,
  connector?: Pick<User, 'connectorRateOverride'>,
): number {
  return connector?.connectorRateOverride ?? settings.connectorRate
}

export function getEffectiveSuperAgencyRate(
  settings: Pick<SystemSettings, 'superAgencyRate'>,
): number {
  return settings.superAgencyRate
}

/** 商品別 → ユーザー個別 → システム全体 の優先順位で代理店報酬率を解決 */
export function getEffectiveAgencyRateForProduct(
  product: Pick<Product, 'agencyRate'>,
  settings: Pick<SystemSettings, 'agencyRate'>,
  agency?: Pick<User, 'agencyRateOverride'>,
): number {
  return product.agencyRate ?? agency?.agencyRateOverride ?? settings.agencyRate
}

/** 商品別 → ユーザー個別 → システム全体 の優先順位でコネクター報酬率を解決 */
export function getEffectiveConnectorRateForProduct(
  product: Pick<Product, 'connectorRate'>,
  settings: Pick<SystemSettings, 'connectorRate'>,
  connector?: Pick<User, 'connectorRateOverride'>,
): number {
  return product.connectorRate ?? connector?.connectorRateOverride ?? settings.connectorRate
}

export type RewardBreakdown = {
  baseAmountJPY: number
  superAgencyAllocationRate: number
  agencyAllocationRate: number
  connectorAllocationRate: number
  superAgencyRewardJPY: number
  agencyRewardJPY: number
  connectorRewardJPY: number
  jnaviShareJPY: number
}

export function calculateRewardBreakdown(args: {
  baseAmountJPY: number
  superAgencyRate: number
  agencyRate: number
  connectorRate: number
  assignedAgencyRole: '代理店' | '総代理店'
  hasSuperAgency?: boolean
  hasConnector?: boolean
}): RewardBreakdown {
  const base = Math.max(0, args.baseAmountJPY)
  const hasConnector = args.hasConnector ?? true
  const hasSuperAgency = args.assignedAgencyRole === '総代理店' ? true : (args.hasSuperAgency ?? false)

  if (hasConnector && args.assignedAgencyRole === '代理店' && args.connectorRate > args.agencyRate) {
    throw new Error('connectorRate must not exceed agencyRate')
  }

  if (args.assignedAgencyRole === '総代理店' && hasConnector && args.connectorRate > args.superAgencyRate) {
    throw new Error('connectorRate must not exceed superAgencyRate')
  }

  if (hasSuperAgency && args.assignedAgencyRole === '代理店' && args.agencyRate > args.superAgencyRate) {
    throw new Error('agencyRate must not exceed superAgencyRate')
  }

  let superAgencyAllocationRate = 0
  let agencyAllocationRate = 0
  let connectorAllocationRate = hasConnector ? args.connectorRate : 0

  if (args.assignedAgencyRole === '総代理店') {
    superAgencyAllocationRate = hasConnector ? args.superAgencyRate - connectorAllocationRate : args.superAgencyRate
  } else if (hasSuperAgency) {
    superAgencyAllocationRate = args.superAgencyRate - args.agencyRate
    agencyAllocationRate = hasConnector ? args.agencyRate - connectorAllocationRate : args.agencyRate
  } else {
    agencyAllocationRate = hasConnector ? args.agencyRate - connectorAllocationRate : args.agencyRate
  }

  superAgencyAllocationRate = normalizeRate(superAgencyAllocationRate)
  agencyAllocationRate = normalizeRate(agencyAllocationRate)
  connectorAllocationRate = normalizeRate(connectorAllocationRate)

  const superAgencyRewardJPY = floorJPY(base * superAgencyAllocationRate)
  const agencyRewardJPY = floorJPY(base * agencyAllocationRate)
  const connectorRewardJPY = hasConnector ? floorJPY(base * connectorAllocationRate) : 0
  const jnaviShareJPY = Math.max(
    0,
    base - superAgencyRewardJPY - agencyRewardJPY - connectorRewardJPY,
  )

  return {
    baseAmountJPY: base,
    superAgencyAllocationRate,
    agencyAllocationRate,
    connectorAllocationRate,
    superAgencyRewardJPY,
    agencyRewardJPY,
    connectorRewardJPY,
    jnaviShareJPY,
  }
}

export function calculateModelAAmounts(args: {
  baseAmountJPY: number
  agencyRate: number
  connectorRate: number
  hasConnector?: boolean
}): {
  baseAmountJPY: number
  agencyRewardJPY: number
  connectorRewardJPY: number
  jnaviShareJPY: number
} {
  const result = calculateRewardBreakdown({
    baseAmountJPY: args.baseAmountJPY,
    superAgencyRate: args.agencyRate,
    agencyRate: args.agencyRate,
    connectorRate: args.connectorRate,
    assignedAgencyRole: '代理店',
    hasSuperAgency: false,
    hasConnector: args.hasConnector,
  })

  return {
    baseAmountJPY: result.baseAmountJPY,
    agencyRewardJPY: result.agencyRewardJPY,
    connectorRewardJPY: result.connectorRewardJPY,
    jnaviShareJPY: result.jnaviShareJPY,
  }
}

export function initialRewardStatusForProductType(productType: ProductType): RewardStatus {
  void productType
  return '未確定'
}
